// Estado de sesión unificado para ambos modos de auth.
//
//  - `legacy`  : JWT HS256 emitido por auth-tenant-svc/v1/public/auth/token,
//                persistido en localStorage. Login con (tenantId, usuario).
//  - `keycloak`: derivado del access token de keycloak-js. Login = redirect
//                a Keycloak. `tenantId` viene del claim `tenantId` mapeado
//                en el realm.
//
// API pública: `session`, `login()`, `logout()`. Los componentes no deben
// preocuparse por el modo activo.

import {
  isKeycloakMode,
  keycloak,
  initKeycloak,
  loginWithPassword,
  restoreKcSession,
  clearKcSession,
  refreshIfNeeded,
  currentTenantId,
  currentUsername,
  currentRoles,
} from './keycloak';
import { isPilotMode, fetchPilotSession } from './pilot';
import { resolveDemoPilotCredentials, demoAdminUser } from './clinic-preview.svelte';

export { isPilotMode } from './pilot';

const LS_KEY = 'toscana.session.v1';
const AUTO_DEMO_LOGIN = import.meta.env.VITE_AUTO_DEMO_LOGIN !== 'false' && !isPilotMode();
const DEMO_USER = import.meta.env.VITE_DEMO_USER ?? 'clinica-demo-medica@toscanasystems.com';
const DEMO_PASS = import.meta.env.VITE_DEMO_PASS ?? 'Demo-Toscana-2026';
const DEMO_TENANT = import.meta.env.VITE_DEMO_TENANT ?? 'clinica-demo-medica';
const SKIP_AUTO_KEY = 'toscana.skipAutoDemo';
const DEMO_TENANT_KEY = 'toscana.demo.tenant';

export type Session = {
  token: string;
  tenantId: string;
  usuario: string;
  expiresAt: number;     // epoch ms
  roles?: string[];
  source: 'legacy' | 'keycloak';
};

// ── Persistencia legacy ─────────────────────────────────────────────────────
function loadLegacy(): Session | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    if (!s.token || !s.tenantId || s.expiresAt < Date.now() + 5_000) return null;
    return { ...s, source: 'legacy' };
  } catch {
    return null;
  }
}

function saveLegacy(s: Session | null) {
  if (s) localStorage.setItem(LS_KEY, JSON.stringify(s));
  else localStorage.removeItem(LS_KEY);
}

/** Asegura roles de admin en el tenant demo si Keycloak no los incluyó en el token. */
function normalizeRoles(roles: string[], usuario: string): string[] {
  const base = [...roles];
  if (
    (usuario === DEMO_USER || usuario.endsWith('@toscanasystems.com'))
    && !base.includes('admin-tenant')
  ) {
    base.push('admin-tenant');
  }
  return base;
}

/** Hidrata sesión Keycloak desde localStorage antes del primer render (sin init() colgado). */
function hydrateKeycloakSession(): Session | null {
  if (!isKeycloakMode() || typeof localStorage === 'undefined') return null;
  if (!restoreKcSession()) return null;
  const k = keycloak();
  if (!k.authenticated || !k.token || !k.tokenParsed?.exp) return null;
  const usuario = currentUsername() ?? '(desconocido)';
  return {
    token: k.token,
    tenantId: currentTenantId() ?? DEMO_TENANT,
    usuario,
    expiresAt: k.tokenParsed.exp * 1000,
    roles: normalizeRoles(currentRoles(), usuario),
    source: 'keycloak',
  };
}

function attachKeycloakHooks(): void {
  const k = keycloak();
  k.onAuthSuccess = () => syncFromKeycloak();
  k.onAuthRefreshSuccess = () => syncFromKeycloak();
  k.onAuthLogout = () => { _session = null; };
}

if (isKeycloakMode()) attachKeycloakHooks();

// ── Rune-based store ────────────────────────────────────────────────────────
let _session = $state<Session | null>(
  isKeycloakMode() ? hydrateKeycloakSession() : loadLegacy(),
);

export const session = {
  get value() { return _session; },
  get isAuth() { return !!_session; },
};

/** Refresca el snapshot rune desde Keycloak (llamar tras init y refresh). */
function syncFromKeycloak() {
  const k = keycloak();
  if (!k.authenticated || !k.token || !k.tokenParsed?.exp) {
    _session = null;
    return;
  }
  _session = {
    token: k.token,
    tenantId: currentTenantId() ?? DEMO_TENANT,
    usuario: currentUsername() ?? '(desconocido)',
    expiresAt: k.tokenParsed.exp * 1000,
    roles: normalizeRoles(currentRoles(), currentUsername() ?? ''),
    source: 'keycloak',
  };
}

async function tryAutoDemoLogin(): Promise<boolean> {
  if (!isKeycloakMode() || !AUTO_DEMO_LOGIN) return false;
  if (sessionStorage.getItem(SKIP_AUTO_KEY)) return false;
  const res = await loginWithPassword(DEMO_USER, DEMO_PASS);
  if (!res.ok) return false;
  syncFromKeycloak();
  return !!_session;
}

/**
 * Bootstrap del SPA. En modo keycloak inicializa el SDK y sincroniza la
 * sesión. En modo legacy es no-op (la sesión se hidrata desde localStorage
 * al evaluar el módulo). Idempotente.
 */
export async function bootstrapAuth(): Promise<void> {
  if (isPilotMode()) {
    let preview: string | null = null;
    let demoTenant: string | null = null;
    try {
      preview = sessionStorage.getItem('toscana.clinicPreview');
      demoTenant = sessionStorage.getItem(DEMO_TENANT_KEY);
    } catch { /* ignore */ }
    if (!preview && !demoTenant) {
      _session = null;
      return;
    }
    const creds = preview
      ? resolveDemoPilotCredentials({ tenantId: DEMO_TENANT, usuario: DEMO_USER })
      : { tenantId: demoTenant!, usuario: demoAdminUser(demoTenant!) };
    const s = await fetchPilotSession(creds.tenantId, creds.usuario);
    _session = s;
    return;
  }
  if (!isKeycloakMode()) return;

  attachKeycloakHooks();

  if (_session) {
    await Promise.race([
      refreshIfNeeded(30),
      new Promise<void>((r) => setTimeout(r, 3_000)),
    ]);
    syncFromKeycloak();
    return;
  }

  if (restoreKcSession()) {
    syncFromKeycloak();
    if (_session) {
      void refreshIfNeeded(30).then(() => syncFromKeycloak());
      return;
    }
  }

  const hasOAuthCallback = /[?#](code|session_state|error)=/.test(
    (typeof window !== 'undefined' ? window.location.hash + window.location.search : ''),
  );
  if (hasOAuthCallback) {
    const ok = await Promise.race([
      initKeycloak(),
      new Promise<boolean>((r) => setTimeout(() => r(false), 4_000)),
    ]);
    if (ok) syncFromKeycloak();
  }

  if (!_session) await tryAutoDemoLogin();
}

/** Renueva sesión piloto y actualiza el store (api.ts en 401). */
export async function refreshPilotSession(): Promise<Session | null> {
  if (!isPilotMode()) return null;
  try {
    let preview: string | null = null;
    let demoTenant: string | null = null;
    try {
      preview = sessionStorage.getItem('toscana.clinicPreview');
      demoTenant = sessionStorage.getItem(DEMO_TENANT_KEY);
    } catch { /* ignore */ }
    const creds = preview
      ? resolveDemoPilotCredentials({ tenantId: DEMO_TENANT, usuario: DEMO_USER })
      : demoTenant
        ? { tenantId: demoTenant, usuario: demoAdminUser(demoTenant) }
        : resolveDemoPilotCredentials({ tenantId: DEMO_TENANT, usuario: DEMO_USER });
    const s = await fetchPilotSession(creds.tenantId, creds.usuario);
    _session = s;
    return s;
  } catch {
    return null;
  }
}

// ── Login / Logout (multi-modo) ─────────────────────────────────────────────
export async function login(
  tenantIdOrEmpty?: string,
  usuarioOrEmpty?: string,
  password?: string,
): Promise<Session | void> {
  if (isPilotMode()) {
    const s = await fetchPilotSession(tenantIdOrEmpty, usuarioOrEmpty);
    _session = s;
    try {
      sessionStorage.setItem(DEMO_TENANT_KEY, s.tenantId);
    } catch { /* ignore */ }
    return s;
  }
  if (isKeycloakMode()) {
    const usuario = (usuarioOrEmpty ?? '').trim();
    const pass = password ?? '';
    if (usuario && pass) {
      const res = await loginWithPassword(usuario, pass);
      if (!res.ok) throw new Error(res.error);
      sessionStorage.removeItem(SKIP_AUTO_KEY);
      syncFromKeycloak();
      const s = _session;
      if (s && tenantIdOrEmpty?.trim()) {
        _session = { ...s, tenantId: tenantIdOrEmpty.trim() };
      }
      return _session ?? undefined;
    }
    throw new Error('Ingrese usuario y contraseña.');
  }
  const tenantId = (tenantIdOrEmpty ?? '').trim();
  const usuario  = (usuarioOrEmpty  ?? '').trim();
  if (!tenantId || !usuario) throw new Error('tenantId y usuario son obligatorios.');

  const r = await fetch('/api/auth/v1/public/auth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ tenantId, usuario })
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(`Login falló: ${r.status} ${r.statusText} — ${txt.slice(0, 200)}`);
  }
  const data = await r.json() as { token: string; tenantId: string; expiresIn: number };
  const s: Session = {
    token: data.token,
    tenantId: data.tenantId ?? tenantId,
    usuario,
    expiresAt: Date.now() + (data.expiresIn ?? 3600) * 1000,
    source: 'legacy',
  };
  _session = s;
  saveLegacy(s);
  return s;
}

export function logout(): void | Promise<void> {
  if (isPilotMode()) {
    _session = null;
    try {
      sessionStorage.removeItem(DEMO_TENANT_KEY);
    } catch { /* ignore */ }
    return;
  }
  if (isKeycloakMode()) {
    sessionStorage.setItem(SKIP_AUTO_KEY, '1');
    _session = null;
    clearKcSession();
    return;
  }
  _session = null;
  saveLegacy(null);
}

export async function getTenantConfig(tenantId: string): Promise<any> {
  const r = await fetch(`/api/auth/v1/public/tenants/${encodeURIComponent(tenantId)}`, {
    headers: { 'Accept': 'application/json' }
  });
  if (!r.ok) throw new Error(`Tenant config: ${r.status}`);
  return r.json();
}
